/**
 * EVERY DISTANCE THE USER SEES OR HEARS, IN ONE PLACE.
 *
 * This file used to be twenty-nine lines that said "the app uses miles + feet
 * everywhere" and a comment hoping for a "future locale toggle". The hope was
 * the problem: about eighteen other places did their own `/ 1609.344` inline
 * rather than call in here, so the single place was never actually single, and
 * a units toggle would have changed some screens and not others.
 *
 * The rule this file exists to enforce (rule 20): a distance is formatted the
 * SAME WAY everywhere it appears, or the app contradicts itself. A card that
 * says 5.5 km beside a voice that says 3.4 miles is worse than either alone.
 *
 * WHAT GOES WHERE:
 *   fmtDistance      what you READ   — "3.4 mi", "500 ft", "5.5 km"
 *   spokenDistance   what you HEAR   — "3.4 miles", "500 feet", "5.5 kilometers"
 *
 * They are separate on purpose. Abbreviations belong on a screen and nowhere
 * near a speech synthesiser, which reads "mi" as a word.
 *
 * Both take an optional system so a caller can force one (tests, previews);
 * left off, they ask unitSettings for whatever this driver chose.
 */

import { resolveUnitSystem, type UnitSystem } from "@/utils/unitSettings";

const METERS_PER_MILE = 1609.344;
const METERS_PER_FOOT = 0.3048;
const METERS_PER_YARD = 0.9144;

/** Below this many miles, a driving distance reads better in the short unit.
 *  0.1 mi is about 160m — the point where "0.1 mi" stops being useful and
 *  "500 ft" starts. Unchanged from the original file. */
const SHORT_UNIT_BELOW_MILES = 0.1;
/** The metric equivalent: under a kilometre, say metres. */
const SHORT_UNIT_BELOW_METERS = 1000;

/**
 * Format metres as a short user-facing distance.
 *
 *   us      120m → "400 ft"    5500m → "3.4 mi"
 *   uk      120m → "130 yd"    5500m → "3.4 mi"
 *   metric  120m → "120 m"     5500m → "5.5 km"
 *
 * Short units round to the nearest 10, because a driver cannot use "437 ft"
 * and a false precision is still false.
 */
export interface FmtOptions {
  /**
   * Override where the short unit gives way to the long one, in METRES.
   *
   * Exists for walking. A 400m walk reads better as "1300 ft" than "0.2 mi",
   * and TripRecap has always drawn that line at 0.3 mi rather than the 0.1 mi
   * a drive uses. Passing the threshold in keeps that judgement at the call
   * site that has the context, instead of teaching this module about travel
   * modes it otherwise knows nothing about.
   */
  shortBelowM?: number;
}

export function fmtDistance(
  meters: number,
  system?: UnitSystem,
  opts: FmtOptions = {},
): string {
  const sys = system ?? resolveUnitSystem();
  const shortBelowM = opts.shortBelowM;
  if (!Number.isFinite(meters) || meters <= 0) {
    return sys === "metric" ? "0 m" : sys === "uk" ? "0 yd" : "0 ft";
  }

  if (sys === "metric") {
    if (meters < (shortBelowM ?? SHORT_UNIT_BELOW_METERS)) {
      // Round FIRST, then re-check the threshold. 999m rounds to 1000, and
      // "1000 m" is not something anyone writes — it is "1.0 km". Testing the
      // raw value and formatting the rounded one is how a boundary bug gets
      // in: the two numbers are on opposite sides of the line.
      const m = Math.max(10, Math.round(meters / 10) * 10);
      if (m < (shortBelowM ?? SHORT_UNIT_BELOW_METERS)) return `${m} m`;
    }
    return `${(meters / 1000).toFixed(1)} km`;
  }

  const miles = meters / METERS_PER_MILE;
  const shortCutoffMiles =
    shortBelowM !== undefined ? shortBelowM / METERS_PER_MILE : SHORT_UNIT_BELOW_MILES;
  if (miles < shortCutoffMiles) {
    if (sys === "uk") {
      const yd = Math.max(10, Math.round(meters / METERS_PER_YARD / 10) * 10);
      return `${yd} yd`;
    }
    // The original floor was 5 ft; kept, because a 3 ft distance rounding to
    // "10 ft" is a bigger lie than showing "5 ft".
    const ft = Math.max(5, Math.round(meters / METERS_PER_FOOT / 10) * 10);
    return `${ft} ft`;
  }
  // One decimal at every magnitude, so a 10.3 mi note does not read as a flat
  // "10 mi" (original comment, still the rule).
  return `${miles.toFixed(1)} mi`;
}

/**
 * The same distance, in words, for the voice.
 *
 *   "In 0.5 miles, turn right"   "In 500 feet, turn right"
 *   "In 500 meters, turn right"  "In 1.2 kilometers, turn right"
 *
 * Singular is handled ("1 mile", not "1 miles") because a synthesiser will say
 * exactly what it is given and "one miles" is the kind of detail that makes an
 * app sound cheap.
 */
export function spokenDistance(
  meters: number,
  system?: UnitSystem,
  opts: FmtOptions = {},
): string {
  const sys = system ?? resolveUnitSystem();
  const shortBelowM = opts.shortBelowM;
  const plural = (n: number, one: string, many: string) =>
    `${n} ${n === 1 ? one : many}`;

  if (!Number.isFinite(meters) || meters <= 0) {
    return sys === "metric" ? "0 meters" : sys === "uk" ? "0 yards" : "0 feet";
  }

  if (sys === "metric") {
    if (meters < (shortBelowM ?? SHORT_UNIT_BELOW_METERS)) {
      return plural(Math.max(10, Math.round(meters / 10) * 10), "meter", "meters");
    }
    const km = Number((meters / 1000).toFixed(meters > 10000 ? 0 : 1));
    return plural(km, "kilometer", "kilometers");
  }

  const miles = meters / METERS_PER_MILE;
  const spokenCutoffMiles =
    shortBelowM !== undefined ? shortBelowM / METERS_PER_MILE : SHORT_UNIT_BELOW_MILES;
  if (miles < spokenCutoffMiles) {
    if (sys === "uk") {
      return plural(Math.max(10, Math.round(meters / METERS_PER_YARD / 10) * 10), "yard", "yards");
    }
    return plural(Math.max(10, Math.round(meters / METERS_PER_FOOT / 10) * 10), "foot", "feet");
  }
  // Two decimals under half a mile, one above — the original navCues rule,
  // moved here so the voice and the screen cannot drift apart.
  const mi = Number(miles.toFixed(meters > 800 ? 1 : 2));
  return plural(mi, "mile", "miles");
}

/** Convert meters → miles (number, unrounded). For internal math when a
 *  component needs the value in miles (e.g. comparing thresholds). NOT for
 *  display — that is what fmtDistance is for. */
export function metersToMiles(meters: number): number {
  return meters / METERS_PER_MILE;
}

/** Convert meters → kilometres, for the same reason. */
export function metersToKm(meters: number): number {
  return meters / 1000;
}

/**
 * The number and the unit, separately, for the few places that style them
 * differently — a big figure with a small suffix on a route card.
 * Same thresholds as fmtDistance, by construction: it calls it and splits.
 */
export function fmtDistanceParts(
  meters: number,
  system?: UnitSystem,
  opts: FmtOptions = {},
): { value: string; unit: string } {
  const s = fmtDistance(meters, system, opts);
  const i = s.lastIndexOf(" ");
  return i < 0 ? { value: s, unit: "" } : { value: s.slice(0, i), unit: s.slice(i + 1) };
}
